import type { ReactNode } from 'react';

import { MarkdownContent } from '@/lib/crm/components/MarkdownContent';

const InlineText = ({ children }: { children?: ReactNode }): ReactNode =>
  children;

const INLINE_MARKDOWN_COMPONENTS = {
  p: InlineText,
  h1: InlineText,
  h2: InlineText,
  h3: InlineText,
  h4: InlineText,
  h5: InlineText,
  h6: InlineText,
  ul: InlineText,
  ol: InlineText,
  li: InlineText,
  blockquote: InlineText,
  pre: InlineText,
  hr: InlineText,
};

interface ClarifyingRichTextProps {
  content: string;
  orgId?: number;
  className?: string;
}

/**
 * Renders a backend clarifying string (which may contain `<Entity …>` markup) as
 * INLINE entity-aware markdown — no block `<p>` — so it is valid inside a `<legend>`,
 * a label `<p>`, or a description `<p>` (all phrasing contexts where a nested block
 * element would be invalid HTML).
 *
 * Assumes clarifying text is a single inline line (plain text + `<Entity …>` tags +
 * optional inline emphasis/links). Block constructs (headings, paragraphs, lists,
 * blockquotes, code blocks) are overridden to render inline via
 * `INLINE_MARKDOWN_COMPONENTS`; table elements are not expected and are not overridden.
 */
export function ClarifyingRichText({
  content,
  orgId,
  className,
}: ClarifyingRichTextProps) {
  return (
    <MarkdownContent
      content={content}
      orgId={orgId}
      size='compact'
      as='span'
      components={INLINE_MARKDOWN_COMPONENTS}
      className={className}
      entityInteractive={false}
    />
  );
}
